/**
 * Helper function to check if book has work in progress
 * @param {Object} book - Book object with lastOrderCreatedAt and updatedAt
 * @returns {boolean} - True if book has pending changes
 */
const hasWorkInProgress = (book) => {
  if (!book.lastOrderCreatedAt) return true; // Never ordered
  const updatedAtTime = new Date(book.updatedAt).getTime();
  const lastOrderTime = new Date(book.lastOrderCreatedAt).getTime();
  const diffInSeconds = (lastOrderTime - updatedAtTime) / 1000;
  return diffInSeconds >= 10;
};

/**
 * Determines the appropriate destination route for a user based on their profile and books
 * @param {Object} profile - User profile data
 * @param {Array} myBooks - Array of user's books
 * @param {string} activeBookId - Currently active book ID
 * @returns {string} - The route to navigate to with query parameters
 */
export const determineUserDestination = (profile, myBooks = [], activeBookId = null) => {
  // No books - go to create book
  if (!myBooks.length) {
    return '/create-book?stepCode=Create_New_Book';
  }

  // If activeBookId exists, check if that book has work in progress
  if (!!activeBookId) {
    const activeBook = myBooks.find(book => book._id === activeBookId);
    
    // If active book is fully ordered (no work in progress), go to dashboard
    if (activeBook && !hasWorkInProgress(activeBook)) {
      return '/dashboard';
    }
  }

  // No active book - go to dashboard
  if (!activeBookId) {
    return '/dashboard';
  }

  // Has books with work in progress - find latest step
  const latestStep = fetchLatestStepCode(profile?.bookData, activeBookId);

  // If on Place_Order step, go to review
  if (latestStep?.code === 'Place_Order') {
    return '/review?stepCode=Review_Your_Book';
  }

  // If has a specific step with route, go there
  if (latestStep?.routeName && latestStep?.code) {
    return `${latestStep.routeName}?stepCode=${latestStep.code}`;
  }

  // Default to dashboard
  return '/dashboard';
};

/**
 * Fetches the latest step code for a specific book
 * @param {Array} bookData - Array of book data from profile
 * @param {string} activeBookId - The active book ID
 * @returns {Object|null} - Latest step object or null
 */
export const fetchLatestStepCode = (bookData = [], activeBookId = null) => {
  const activeBook = bookData.find((book) => book._id === activeBookId);
  return activeBook?.latestStepCode ?? null;
};

/**
 * Loads all necessary user data (profile, books, images) and prepares Redux state
 * @param {string} userId - User ID
 * @param {Function} dispatch - Redux dispatch function
 * @param {boolean} isComingFromNonUserRoute - Flag to skip certain operations
 * @returns {Promise<Object>} - Returns { profile, myBooks, activeBookId }
 */
export const loadUserData = async (userId, dispatch, isComingFromNonUserRoute = false) => {
  const { profileDetails } = await import('../services/ProfileService');
  const { booksGet } = await import('../services/bookService');
  const { fetchBatchImagesByBookId } = await import('../services/mediaService');
  const { setMyBooks, updateProfile, setBooksLoading } = await import('../redux/slice/profileSlice');
  const { setActiveBookId } = await import('../redux/slice/stepSlice');
  const { setCoverImages, setImages } = await import('../redux/slice/sketchSlice');

  // Helper function to check if book has work in progress
  const hasWorkInProgress = (book) => {
    if (!book.lastOrderCreatedAt) return true; // Never ordered
    const updatedAtTime = new Date(book.updatedAt).getTime();
    const lastOrderTime = new Date(book.lastOrderCreatedAt).getTime();
    const diffInSeconds = (lastOrderTime - updatedAtTime) / 1000;
    return diffInSeconds < 10;
  };

  // Fetch profile
  const profileRes = await profileDetails(userId);
  const profile = profileRes?.data || null;
  const profileBooks = profile?.bookData || [];

  // Fetch books
  const bookRes = await booksGet(userId);
  let myBooks = [];
  let activeBookId = null;

  if (bookRes?.docs?.data?.length) {
    // Merge books with profile data
    myBooks = bookRes.docs.data.map(item => {
      const profileBook = profileBooks.find(({ _id }) => item._id === _id);
      const isOrderPlaces = profileBook?.isOrderPlaces;
      const completedStepData = profileBook?.completedSteps || [];
      const completedSteps = completedStepData
        .filter(step => step?.isCompleted === 'yes')
        .map(step => step.code);
      
      return { 
        ...item, 
        isOrderPlaces: !!isOrderPlaces, 
        completedSteps 
      };
    });

    dispatch(setMyBooks(myBooks));

    // Determine active book
    let targetBookId = profile.latestBookId ?? null;

    if (!targetBookId && !isComingFromNonUserRoute && profileBooks.length) {
      // Find last unordered book
      const unorderedBooks = profileBooks.filter(book => !book.isOrderPlaces);
      if (unorderedBooks.length > 0) {
        const lastUnorderedBook = unorderedBooks[unorderedBooks.length - 1];
        targetBookId = lastUnorderedBook._id || lastUnorderedBook.id;
      }
    }

    // Validate targetBookId has work in progress before setting as active
    if (targetBookId && !isComingFromNonUserRoute) {
      const targetBook = myBooks.find(book => book._id === targetBookId);      
      // Only set as active if book has work in progress
      if (targetBook && hasWorkInProgress(targetBook)) {
        activeBookId = targetBookId;
        dispatch(setActiveBookId({ bookId: targetBookId }));

        // Fetch images for active book
        try {
          const batchImageRes = await fetchBatchImagesByBookId(targetBookId);
          if (batchImageRes?.docs?.data?.length) {
            dispatch(
              setCoverImages(
                batchImageRes.docs.data.filter((image) => !!image.isCover)
              )
            );
            dispatch(
              setImages(
                batchImageRes.docs.data.filter((image) => !image.isCover)
              )
            );
          }
        } catch (error) {
          console.error('Error fetching book images:', error);
        }
      }
    } else {
      targetBookId = null
    }
  }

  // Update profile in Redux
  if (profileBooks.length) {
    dispatch(updateProfile({ bookData: profileBooks }));
  }

  dispatch(setBooksLoading(false));

  return { profile, myBooks, activeBookId };
};